import z from "zod";
import {
  isBlank,
  nullishNumber,
  nullishString,
  requiredEmail,
  requiredNumber,
  requiredString,
  toNumber,
} from "./schemaValidators";

const phoneSchema = requiredString("Укажите телефон");

export const ProductSchema = z
  .object({
    okpdCode: nullishString(),
    okpdName: nullishString(),
  })
  .optional()
  .nullable();

// export const IKZSchema = nullishString();
const IKZSchema = z.preprocess((v) => {
  if (!Array.isArray(v)) return v;
  // выкинем null/undefined/"" из массива
  return v.filter((x) => x != null && x !== "");
}, z.array(z.string().trim()).optional().default([]));

export const BeneficiariesContractSchema = z
  .object({
    concluded: z.boolean().optional(),
    contractNumber: nullishString().optional(),
    contractDate: nullishString().optional(),
    regNumber: nullishString().optional(),
    contractAmount: nullishNumber().optional(),
  })
  .optional()
  .nullable()
  .superRefine((val, ctx) => {
    if (!val || val.concluded !== true) return; // <- ранний выход

    if (isBlank(val.contractNumber)) {
      ctx.addIssue({
        path: ["contractNumber"],
        code: "custom",
        message: "Укажите номер контракта",
      });
    }
    if (isBlank(val.contractDate)) {
      ctx.addIssue({ path: ["contractDate"], code: "custom", message: "Укажите дату контракта" });
    }
    if (isBlank(val.regNumber)) {
      ctx.addIssue({ path: ["regNumber"], code: "custom", message: "Укажите реестровый номер" });
    }
    const amount = toNumber(val.contractAmount);
    if (!Number.isFinite(amount) || amount <= 0) {
      ctx.addIssue({
        path: ["contractAmount"],
        code: "custom",
        message: "Укажите сумму контракта",
      });
    }
  });

export const BeneficiariesSchema = z.object({
  inn: requiredString(),
  kpp: requiredString(),
  ogrn: requiredString(),
  displayName: requiredString("Укажите наименование бенефициара"),
  customerRegNum: requiredNumber(),
  bgAmount: requiredNumber(),
  deliveryPlace: requiredString("Укажите место доставки"),
  purchaseAmount: requiredNumber(),
  actualAddress: requiredString("Укажите адрес"),
  phone: phoneSchema,
  email: requiredEmail(),
  contract: BeneficiariesContractSchema.optional(),
});

export const ContractConditionsSchema = z
  .object({
    requiredIndisputableDebiting: z.boolean().optional(),
    prepaymentExists: z.boolean().optional(),
    prepaymentAmount: nullishNumber().optional(),
    includedForfeit: z.boolean().optional(),
    requiredSecurityForGuaranteePeriod: z.boolean().optional(),
    securityForGuaranteePeriodDays: nullishNumber().optional(),
    securityForGuaranteePeriodAmount: nullishNumber().optional(),
  })
  .superRefine((val, ctx) => {
    if (val.prepaymentExists && !val.prepaymentAmount) {
      ctx.addIssue({ path: ["prepaymentAmount"], code: "custom", message: "Укажите сумму аванса" });
    }
    if (val.requiredSecurityForGuaranteePeriod) {
      const noDays = !val.securityForGuaranteePeriodDays;
      const noAmt = !val.securityForGuaranteePeriodAmount;
      if (noDays) {
        ctx.addIssue({
          path: ["securityForGuaranteePeriodDays"],
          code: "custom",
          message: "Укажите дни или сумму обеспечения",
        });
      }
      if (noAmt) {
        ctx.addIssue({
          path: ["securityForGuaranteePeriodAmount"],
          code: "custom",
          message: "Укажите дни или сумму обеспечения",
        });
      }
    }
  });

export const PurchaseLotSchema = z.object({
  id: z.number().int().optional(),
  lotNumber: requiredNumber(),
  lotSubject: requiredString(),
  startAmount: requiredNumber(),
  principalOfferAmount: requiredNumber(),
  ikzList: IKZSchema,
  products: z.array(ProductSchema).nullable().optional(),
  contractConditions: ContractConditionsSchema,
  beneficiaries: z.array(BeneficiariesSchema),
});

export const PurchaseSchema = z.object({
  purchaseNumber: requiredString(),
  marketPlace: requiredString(),
  auctionType: requiredString(),
  tenderType: requiredString(),
  law: requiredString(),
  tenderDate: requiredString(),
  purchaseLink: requiredString(),
  purchaseSubject: requiredString(),
  lots: z.array(PurchaseLotSchema),
});

export const BgFormSchema = z.object({
  principal: z.object({
    inn: requiredString()
  }),
  guarantee: z.object({
    type: requiredString(),
    startDate: requiredString(),
    endDate: requiredString(),
    amount: requiredNumber(),
  }),
  purchase: PurchaseSchema,
  requestedBanks: z.array(z.string()),
});

export type BgFormSchemaType = z.infer<typeof BgFormSchema>;
export type BgFormInput = z.input<typeof BgFormSchema>; // что допускается на ВХОД (можно null)
export type BgFormOutput = z.output<typeof BgFormSchema>; // что получаем на ВЫХОДЕ (валидные типы)

export type ProductInput = z.input<typeof ProductSchema>;
export type BeneficiaryInput = z.input<typeof BeneficiariesSchema>;
export type PurchaseLotInput = z.input<typeof PurchaseLotSchema>;
export type PurchaseInput = z.input<typeof PurchaseSchema>;
